import { Fragment, useCallback, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ExternalLink, RefreshCw, Search, Server } from 'lucide-react';
import {
  api,
  staleNotice,
  type GatewayActionDef,
  type GatewayCommandRecord,
  type GatewayFleet,
  type GatewayLink,
  type GatewayRoute,
  type GatewayRouter,
  type GatewaySendResult,
} from '../lib/api';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Drawer } from '../components/ui/Drawer';
import { Fact, FactSection } from '../components/ui/Facts';
import { InfoTip } from '../components/ui/InfoTip';
import { LoadingTicker } from '../components/LoadingTicker';
import { HistoryItem } from '../components/GatewayCommandView';
import { isGoodVerdict } from '../lib/gateway-verdict';
import { actionForGateway, smsUsable } from '../lib/gateway-actions';
import { RefreshProgress } from '../components/RefreshProgress';
import { useSnapshotRefresh } from '../lib/use-snapshot-refresh';
import { SortableHeader } from '../components/ui/SortableHeader';
import { useSort } from '../lib/sort';
import { formatDateTime, formatExact, formatNumber, formatRelative } from '../lib/format';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/use-auth';

type Filter = 'attention' | 'mobile' | 'silent' | 'misconfigured' | 'all';

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'attention', label: 'Needs attention' },
  { value: 'mobile', label: 'On cellular' },
  { value: 'silent', label: 'Not reporting' },
  { value: 'misconfigured', label: 'Misconfigured' },
  { value: 'all', label: 'All' },
];

const LINK_LABEL: Record<GatewayLink, string> = {
  mobile: 'On cellular',
  wired: 'Wired',
  silent: 'Not reporting',
  unknown: 'Unknown',
};

const LINK_EXPLAINER = (
  <>
    <p>
      <b>On cellular</b>: RMS reports the gateway's internet going over its SIM, and it checked in within 2 hours.
    </p>
    <p className="mt-1">
      <b>Not reporting</b>: RMS has not heard from the gateway in over 2 hours, so its last state is history. That is
      not the same as offline — the gateway may be up and routing traffic with only its RMS check-in failing (RMS
      turned off or blocked, a firmware or clock problem), or it may be down. One case matters most: a wired line that
      is up but passing no traffic, with no failover, looks exactly like this — and RMS cannot reach it, which is when
      the SMS commands matter.
    </p>
  </>
);

// Replies keep arriving for a while after an SMS; the server stops collecting
// at 15 minutes, so the page stops asking a little after that.
const POLL_MS = 5_000;

function utcIso(value: string | null | undefined): string | null {
  return value ? `${value.replace(' ', 'T')}Z` : null;
}

function customerOf(r: GatewayRouter): string | null {
  return r.organization?.organizationName ?? r.companyName;
}

function problems(r: GatewayRouter) {
  return r.checks.filter((c) => c.severity === 'problem');
}

function matchesFilter(r: GatewayRouter, filter: Filter): boolean {
  if (filter === 'all') return true;
  if (filter === 'mobile') return r.link === 'mobile';
  if (filter === 'silent') return r.link === 'silent';
  if (filter === 'misconfigured') return problems(r).length > 0;
  return r.link === 'mobile' || r.link === 'silent' || problems(r).length > 0;
}

const LINK_RANK: Record<GatewayLink, number> = { mobile: 0, silent: 1, unknown: 2, wired: 3 };

function getRouterValue(r: GatewayRouter, key: string): unknown {
  switch (key) {
    case 'name':
      return r.name.toLowerCase();
    case 'customer':
      return customerOf(r)?.toLowerCase() ?? null;
    case 'link':
      return LINK_RANK[r.link];
    case 'seen':
      return r.lastUpdateAt;
    case 'checks':
      return -problems(r).length;
    default:
      return null;
  }
}

const NO_CUSTOMER = 'No customer';

interface CustomerGroup {
  label: string;
  unassigned: boolean;
  rows: GatewayRouter[];
  mobile: number;
  silent: number;
}

const byName = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });

/** Customers A–Z (or Z–A), each one's gateways A–Z; gateways with no customer last either way. */
function groupByCustomer(rows: GatewayRouter[], descending: boolean): CustomerGroup[] {
  const groups = new Map<string, CustomerGroup>();
  for (const r of rows) {
    const label = customerOf(r) ?? NO_CUSTOMER;
    const g = groups.get(label) ?? { label, unassigned: customerOf(r) === null, rows: [], mobile: 0, silent: 0 };
    g.rows.push(r);
    if (r.link === 'mobile') g.mobile++;
    if (r.link === 'silent') g.silent++;
    groups.set(label, g);
  }
  for (const g of groups.values()) g.rows.sort((a, b) => byName(a.name, b.name));
  return [...groups.values()].sort((a, b) => {
    if (a.unassigned !== b.unassigned) return a.unassigned ? 1 : -1;
    return descending ? byName(b.label, a.label) : byName(a.label, b.label);
  });
}

// What a refresh does, in order. The reads run in parallel on the server, so
// this is the shape of the work rather than live progress.
const LOAD_STEPS = [
  'Checking the shared snapshot…',
  'Asking Teltonika RMS where every gateway is…',
  'Reading Hologram SIMs and usage…',
  'Matching SIMs to gateways by ICCID…',
  'Looking up X5 servers and customers…',
];
const SLOW_MESSAGE =
  'Hologram is slow to answer — still waiting. If it does not reply, the last good copy is shown instead.';

function isOpen(c: GatewayCommandRecord): boolean {
  return !c.settledAt && (c.status === 'sending' || c.status === 'pending' || c.status === 'sent' || c.status === 'replied');
}

export function Gateways() {
  const [filter, setFilter] = useState<Filter>('attention');
  const [q, setQ] = useState('');
  // The open gateway lives in the URL, so a link from Command History (or a
  // pasted one) lands on its drawer.
  const [params, setParams] = useSearchParams();
  const openId = /^\d+$/.test(params.get('gateway') ?? '') ? Number(params.get('gateway')) : null;
  const setOpenId = useCallback(
    (id: number | null) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (id === null) next.delete('gateway');
          else next.set('gateway', String(id));
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );

  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['gateways'],
    queryFn: () => api.listGateways(),
    staleTime: 5 * 60 * 1000,
  });
  // Both pages share one snapshot, so a finished refresh updates the other's cache too.
  const reload = useCallback(
    async (since: number) => {
      queryClient.setQueryData(['gateways'], await api.listGateways(since || undefined));
      await queryClient.invalidateQueries({ queryKey: ['cellular', 'sims'] });
    },
    [queryClient],
  );
  const refresh = useSnapshotRefresh(query.data?.refreshRunId, reload);

  const data = query.data;
  const needle = q.trim().toLowerCase();
  const rows = useMemo(
    () =>
      (data?.routers ?? []).filter(
        (r) =>
          matchesFilter(r, filter) &&
          (!needle ||
            [r.name, customerOf(r), r.serial, r.serverIp, r.server?.name, r.sim?.name, r.sim?.iccid]
              .some((v) => v?.toLowerCase().includes(needle))),
      ),
    [data, filter, needle],
  );
  // Grouped by customer, A–Z, with each customer's gateways A–Z, unless a
  // column header picks a flat sort. Clearing a sort comes back here.
  const sort = useSort(rows, getRouterValue, { key: 'customer', direction: 'asc' });
  const grouped = sort.sort.key === 'customer' || sort.sort.key === null;
  const groups = useMemo(
    () => groupByCustomer(rows, sort.sort.key === 'customer' && sort.sort.direction === 'desc'),
    [rows, sort.sort],
  );
  const loading = query.isLoading;
  const openRouter = data?.routers.find((r) => r.id === openId) ?? null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-2xl font-bold tracking-tight">Gateways</h1>
        <div className="flex items-center gap-3">
          <LoadingTicker active={loading} steps={LOAD_STEPS} slowMessage={SLOW_MESSAGE} />
          {data && !loading && (
            <span className="text-xs text-muted-foreground" title={`RMS and Hologram snapshot taken ${formatDateTime(data.asOf)}.`}>
              Data from {formatRelative(data.asOf)}
            </span>
          )}
          {data?.wireguardDashboardUrl && <WireguardDashboardLink href={data.wireguardDashboardUrl} />}
          <Button
            variant="outline"
            size="sm"
            onClick={() => refresh.start()}
            disabled={refresh.running || query.isLoading}
            title="Re-read Teltonika RMS and Hologram now"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', refresh.running && 'animate-spin')} />
            {refresh.running ? 'Refreshing…' : 'Refresh'}
          </Button>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <Tile label="Gateways" value={formatNumber(data.summary.routers)} hint="Known to RMS" />
          <Tile label="On cellular now" value={formatNumber(data.summary.onMobile)} tone="warn" info={LINK_EXPLAINER} />
          <Tile label="Not reporting" value={formatNumber(data.summary.silent)} tone="caution" hint="No RMS check-in in 2h" info={LINK_EXPLAINER} />
          <Tile
            label="Misconfigured"
            value={formatNumber(data.summary.misconfigured)}
            tone="caution"
            hint="Server, SIM or customer link wrong"
          />
          <Tile
            label="Reachable by SMS"
            value={formatNumber(data.summary.smsReachable)}
            hint="A live Hologram SIM inside"
          />
        </div>
      )}

      {query.error && <p className="text-sm text-destructive">{(query.error as Error).message}</p>}
      {refresh.error && <p className="text-sm text-destructive">{(refresh.error as Error).message}</p>}
      <RefreshProgress progress={refresh.progress} />
      {data && <Notices data={data} />}

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3">
          <div className="relative w-72">
            <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search gateway, customer, server, SIM…"
              className="pl-9"
            />
          </div>
          {<Segmented value={filter} onChange={setFilter} options={FILTERS} ariaLabel="Filter" />}
          <span className="ml-auto text-xs text-muted-foreground">
            {query.isLoading ? '' : `${formatNumber(rows.length)} shown`}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-line bg-muted/50">
              <tr>
                <SortableHeader label="Gateway" sortKey="name" sort={sort.sort} onToggle={sort.toggle} />
                <SortableHeader label="Customer · server" sortKey="customer" sort={sort.sort} onToggle={sort.toggle} />
                <SortableHeader
                  label="Link"
                  sortKey="link"
                  sort={sort.sort}
                  onToggle={sort.toggle}
                  extra={<InfoTip label="What the link states mean">{LINK_EXPLAINER}</InfoTip>}
                />
                <SortableHeader label="Last seen" sortKey="seen" sort={sort.sort} onToggle={sort.toggle} />
                <th className="px-4 py-2 text-left font-medium text-muted-foreground">SIM</th>
                <SortableHeader label="Configuration" sortKey="checks" sort={sort.sort} onToggle={sort.toggle} />
              </tr>
            </thead>
            <tbody>
              {query.isLoading && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center">
                    <LoadingTicker active steps={LOAD_STEPS} slowMessage={SLOW_MESSAGE} />
                  </td>
                </tr>
              )}
              {grouped
                ? groups.map((g) => (
                    <Fragment key={g.label}>
                      <tr className="border-b border-line-soft bg-panel-2">
                        <td colSpan={6} className="px-4 py-1.5">
                          <span className={cn('font-semibold', g.unassigned && 'text-muted-foreground')}>{g.label}</span>
                          <span className="ml-2 text-xs text-muted-foreground">
                            {formatNumber(g.rows.length)} {g.rows.length === 1 ? 'gateway' : 'gateways'}
                            {g.mobile > 0 && <span className="ml-2 text-warn">{g.mobile} on cellular</span>}
                            {g.silent > 0 && <span className="ml-2 text-caution">{g.silent} not reporting</span>}
                          </span>
                        </td>
                      </tr>
                      {g.rows.map((r) => (
                        <GatewayRow key={r.id} r={r} grouped onOpen={setOpenId} />
                      ))}
                    </Fragment>
                  ))
                : sort.sortedItems.map((r) => <GatewayRow key={r.id} r={r} grouped={false} onOpen={setOpenId} />)}
              {!query.isLoading && sort.sortedItems.length === 0 && (
                <EmptyRow
                  cols={6}
                  text={filter === 'attention' ? 'Every gateway is wired, reporting, and correctly configured.' : 'No gateways match.'}
                />
              )}
            </tbody>
          </table>

        </div>
      </Card>

      <Drawer
        open={openId !== null}
        onClose={() => setOpenId(null)}
        className="max-w-2xl"
        title={
          openRouter ? (
            <div>
              <div className="flex items-center gap-2">
                <span className="text-base font-semibold">{openRouter.name}</span>
                <LinkBadge link={openRouter.link} />
              </div>
              <div className="text-xs text-muted-foreground">
                {[customerOf(openRouter), openRouter.model, openRouter.serial && `S/N ${openRouter.serial}`]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
          ) : (
            <span className="text-base font-semibold">Gateway {openId}</span>
          )
        }
      >
        {openRouter && data ? (
          <RouterDetail
            key={openRouter.id}
            router={openRouter}
            actions={data.actions}
            dashboardUrl={data.wireguardDashboardUrl}
          />
        ) : (
          <p className="text-sm text-muted-foreground">This gateway is not in the current RMS snapshot.</p>
        )}
      </Drawer>
    </div>
  );
}

function GatewayRow({ r, grouped, onOpen }: { r: GatewayRouter; grouped: boolean; onOpen: (id: number) => void }) {
  return (
    <tr
      onClick={() => onOpen(r.id)}
      className={cn(
        'cursor-pointer border-b border-line-soft last:border-0 hover:bg-raise',
        r.link === 'mobile' && 'shadow-[inset_2px_0_0_var(--color-warn)]',
        r.link === 'silent' && 'shadow-[inset_2px_0_0_var(--color-caution)]',
      )}
    >
      <td className="px-4 py-2">
        <div className="font-medium">
          {r.name}
          {!r.x5 && <NotX5Badge />}
        </div>
        <div className="text-xs text-muted-foreground">{r.model ?? '—'}</div>
      </td>
      <td className="px-4 py-2">
        {/* Grouped, the customer is the heading above; the server is what tells sites apart. */}
        {!grouped && (
          <div className={cn('max-w-56 truncate', !r.organization && 'text-muted-foreground')}>
            {customerOf(r) ?? '—'}
          </div>
        )}
        <div className={cn('text-xs text-muted-foreground', grouped && 'text-sm')}>
          {r.server?.name ?? r.serverIp ?? ''}
        </div>
      </td>
      <td className="px-4 py-2 text-xs">
        <LinkBadge link={r.link} />
        {r.link === 'mobile' && r.mobileSince && (
          <div className="mt-0.5 text-muted-foreground">
            since {r.mobileSinceBeyond ? 'before ' : ''}
            {formatRelative(utcIso(r.mobileSince))}
          </div>
        )}
      </td>
      <td className="px-4 py-2 text-xs text-muted-foreground" title={formatDateTime(utcIso(r.lastUpdateAt))}>
        {r.lastUpdateAt ? formatRelative(utcIso(r.lastUpdateAt)) : 'never'}
      </td>
      <td className="px-4 py-2 text-xs">
        {r.sim ? (
          <span className={cn(!r.smsReachable && 'text-caution')}>{r.sim.name}</span>
        ) : (
          <span className="text-muted-foreground">none</span>
        )}
      </td>
      <td className="px-4 py-2 text-xs">
        <ChecksCell router={r} />
      </td>
    </tr>
  );
}

function Notices({ data }: { data: GatewayFleet }) {
  const notes: string[] = [];
  if (!data.routersAvailable) notes.push('RMS could not be read on the last refresh; gateway states are from an earlier one.');
  if (data.refreshFailed) {
    notes.push(`RMS and Hologram could not be re-read just now, so this is the last good snapshot, from ${formatRelative(data.asOf)}.`);
  }
  const stale = staleNotice(data.staleParts ?? [], formatRelative);
  if (stale) notes.push(stale);
  if (data.directoryPartial) notes.push('The server directory was only partly read, so some customers and servers may be missing.');
  if (data.truncated) notes.push('A provider listing hit its page cap; some gateways or SIMs may be missing.');
  if (notes.length === 0) return null;
  return (
    <div className="space-y-1 rounded-md border border-caution/40 bg-caution/5 px-3 py-2 text-xs">
      {notes.map((n) => (
        <p key={n} className="flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-caution" />
          {n}
        </p>
      ))}
    </div>
  );
}

function ChecksCell({ router }: { router: GatewayRouter }) {
  const bad = problems(router);
  const first = bad[0];
  if (!first) return <span className="text-muted-foreground">OK</span>;
  return (
    <div className="max-w-72" title={bad.map((c) => c.message).join('\n')}>
      <div className="truncate text-caution">{first.message}</div>
      {bad.length > 1 && <div className="text-muted-foreground">+{bad.length - 1} more</div>}
    </div>
  );
}

function RouterDetail({
  router,
  actions,
  dashboardUrl,
}: {
  router: GatewayRouter;
  actions: GatewayActionDef[];
  dashboardUrl: string | null;
}) {
  const history = useQuery({
    queryKey: ['gateways', 'history', router.id],
    queryFn: () => api.listGatewayCommands(router.id),
    // Reading the history is what collects replies, so keep reading while
    // anything is still waiting on one.
    refetchInterval: (q) => (q.state.data?.items.some(isOpen) ? POLL_MS : false),
  });

  const lastWireguard = history.data?.items.find((c) => c.commandId === 'rms.wireguard' && c.status === 'done');

  return (
    <div className="space-y-6">
      <FactSection title="Status">
        <Fact
          label="WireGuard"
          value={
            lastWireguard ? (
              <span className={cn(!isGoodVerdict(lastWireguard.verdict) && 'text-caution')}>
                {lastWireguard.summary}{' '}
                <span className="text-muted-foreground" title={formatExact(lastWireguard.timestamp)}>
                  (checked {formatRelative(lastWireguard.timestamp)})
                </span>
              </span>
            ) : (
              <span className="text-muted-foreground">Not checked yet — run WireGuard below.</span>
            )
          }
        />
        {dashboardUrl && (
          <Fact label="Fleet view" value={<WireguardDashboardLink href={dashboardUrl} />} />
        )}
        <Fact label="Link" value={LINK_LABEL[router.link]} />
        {router.link === 'mobile' && (
          <Fact
            label="On cellular since"
            value={
              router.mobileSince
                ? `${router.mobileSinceBeyond ? 'before ' : ''}${formatDateTime(utcIso(router.mobileSince))}`
                : null
            }
          />
        )}
        <Fact label="Last heard by RMS" value={router.lastUpdateAt ? formatDateTime(utcIso(router.lastUpdateAt)) : 'never'} />
        <Fact label="Cellular" value={[router.connectionType, router.operator].filter(Boolean).join(' · ')} />
        <Fact label="Signal" value={router.signal !== null ? `${router.signal} dBm` : null} />
      </FactSection>

      <FactSection title="Configuration">
        <div className="space-y-1.5 py-2">
          {router.checks.length === 0 && (
            <p className="flex items-center gap-1.5 text-sm">
              <CheckCircle2 className="h-4 w-4 text-ok" />
              {router.x5
                ? 'Tied to a live SIM, a running server and a customer.'
                : 'Tied to a live SIM. Not an X5 site, so no X5 server is expected.'}
            </p>
          )}
          {router.checks.map((c) => (
            <p key={c.code} className={cn('flex items-start gap-1.5 text-sm', c.severity === 'problem' ? 'text-caution' : 'text-muted-foreground')}>
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {c.message}
            </p>
          ))}
        </div>
        <Fact label="Customer" value={router.organization?.organizationName} />
        {router.organizationCandidates.length > 0 && (
          <Fact label="Customers on server" value={router.organizationCandidates.map((o) => o.organizationName).join(', ')} />
        )}
        <Fact label="RMS company" value={router.companyName} />
        <Fact
          label="X5 server"
          value={
            router.serverIp ? (
              <span className="inline-flex items-center gap-1">
                <Server className="h-3.5 w-3.5 text-muted-foreground" />
                {router.server?.name ?? 'no instance'} · <span className="font-mono text-xs">{router.serverIp}</span>
                {router.server && router.server.state !== 'running' && <span className="text-caution">({router.server.state})</span>}
              </span>
            ) : null
          }
        />
        <Fact label="SIM" value={router.sim ? `${router.sim.name} · ${router.sim.state}` : null} />
        <Fact label="ICCID" value={router.sim?.iccid} mono />
        <Fact label="Paired by" value={router.sim ? (router.sim.matchedBy === 'iccid' ? 'ICCID' : 'IMEI (best guess)') : null} />
      </FactSection>

      <CommandPanel router={router} actions={actions} />

      <div>
        <h3 className="pb-1.5 text-sm font-semibold">History</h3>
        {history.isLoading && <p className="text-xs text-muted-foreground">Loading…</p>}
        {history.error && <p className="text-xs text-destructive">{(history.error as Error).message}</p>}
        {history.data?.items.length === 0 && <p className="text-xs text-muted-foreground">No commands sent to this gateway yet.</p>}
        <ul className="space-y-2">
          {history.data?.items.map((c) => <HistoryItem key={c.sentAt} record={c} />)}
        </ul>
      </div>
    </div>
  );
}

function CommandPanel({ router, actions }: { router: GatewayRouter; actions: GatewayActionDef[] }) {
  const { can } = useAuth();
  const [chosen, setChosen] = useState<GatewayActionDef | null>(null);
  const [lastResult, setLastResult] = useState<{ action: GatewayActionDef; result: GatewaySendResult } | null>(null);
  const allowed = actions.filter((a) => can(a.permission)).map((a) => actionForGateway(a, router));
  if (allowed.length === 0) return null;

  const unavailable = (a: GatewayActionDef): string | null => {
    const smsOk = smsUsable(a, router);
    if (!a.rms && !smsOk) return router.smsReachable ? 'RMS has no serial number for this router.' : 'No live Hologram SIM in this router.';
    return null;
  };

  return (
    <div>
      <h3 className="flex items-center gap-1.5 pb-1.5 text-sm font-semibold">
        Commands
        <InfoTip label="How commands are sent">
          <p>
            Most commands exist twice: as a shell command run through <b>RMS</b>, and as a text to the gateway's SMS rules
            through <b>Hologram</b>. By default RMS is tried first — it needs no gateway password and returns the real
            output — and SMS is the fallback for when RMS cannot reach the gateway (a wired line that is up but passing
            no traffic). Every attempt is recorded in the gateway's history and the audit log before it is sent.
          </p>
        </InfoTip>
      </h3>
      <div className="space-y-3 border-t border-line-soft pt-3">
        <div className="flex flex-wrap gap-2">
          {allowed.map((a) => {
            const why = unavailable(a);
            return (
              <Button
                key={a.id}
                size="sm"
                variant={chosen?.id === a.id ? 'secondary' : 'outline'}
                className={cn(a.effect === 'write' && 'text-caution')}
                disabled={why !== null}
                title={why ?? a.description}
                onClick={() => {
                  setLastResult(null);
                  setChosen(chosen?.id === a.id ? null : a);
                }}
              >
                {a.label}
                <span className="text-[10px] font-normal text-muted-foreground">
                  {[a.rms && 'RMS', a.sms && 'SMS'].filter(Boolean).join(' · ')}
                </span>
              </Button>
            );
          })}
        </div>
        {chosen && (
          <SendForm
            key={chosen.id}
            router={router}
            action={chosen}
            onDone={(result) => {
              setLastResult({ action: chosen, result });
              setChosen(null);
            }}
            onCancel={() => setChosen(null)}
          />
        )}
        {lastResult && (
          <ResultNote
            label={lastResult.action.label}
            result={lastResult.result}
            next={nextSteps(lastResult.action, lastResult.result, allowed)}
            onVerify={(a) => {
              setLastResult(null);
              setChosen(a);
            }}
          />
        )}
      </div>
    </div>
  );
}

interface NextStep {
  note: string;
  action: GatewayActionDef;
}

/**
 * What the runbook does after each step. Over SMS a WAN change is saved but
 * only applies when the gateway restarts, so the next step is a reboot; over
 * RMS it applies at once. Either way the last question is whether the
 * WireGuard tunnel came back on the new link.
 */
function nextSteps(action: GatewayActionDef, result: GatewaySendResult, allowed: GatewayActionDef[]): NextStep | null {
  if (result.outcome === 'failed') return null;
  const via = result.attempts.at(-1)?.channel;
  const find = (id: GatewayActionDef['id']) => allowed.find((a) => a.id === id);
  if (action.id === 'wan-disable' || action.id === 'wan-enable') {
    const reboot = find('reboot');
    if (via === 'sms' && reboot) {
      return {
        note: 'Over SMS the change is saved on the gateway but only applies when it restarts. Once it confirms ("Created new option…"), reboot it:',
        action: reboot,
      };
    }
    const wg = find('wireguard');
    return wg ? { note: 'Give the tunnel about a minute to re-establish on the new link, then:', action: wg } : null;
  }
  if (action.id === 'reboot') {
    const wg = find('wireguard');
    return wg
      ? {
          note: 'The gateway is restarting — about two minutes. Its status text will arrive in the history below; once it is back:',
          action: wg,
        }
      : null;
  }
  return null;
}

function ResultNote({
  label,
  result,
  next,
  onVerify,
}: {
  label: string;
  result: GatewaySendResult;
  next: NextStep | null;
  onVerify: (action: GatewayActionDef) => void;
}) {
  const last = result.attempts.at(-1);
  const headline =
    result.outcome === 'done'
      ? `${label} ran over RMS.`
      : result.outcome === 'awaiting'
        ? last?.channel === 'sms'
          ? `${label} sent by SMS — the reply will appear in the history below.`
          : `${label} is running over RMS — its result will appear in the history below.`
        : `${label} did not go through.`;
  return (
    <div
      className={cn(
        'rounded-md border px-3 py-2 text-xs',
        result.outcome === 'failed' ? 'border-destructive/40 bg-destructive/5' : 'border-line bg-raise',
      )}
    >
      <p className="font-medium">{headline}</p>
      {result.notes.map((n) => (
        <p key={n} className="mt-0.5 text-muted-foreground">
          {n}
        </p>
      ))}
      {next && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground">{next.note}</span>
          <Button
            size="sm"
            variant="outline"
            className={cn(next.action.effect === 'write' && 'text-caution')}
            onClick={() => onVerify(next.action)}
          >
            {next.action.id === 'wireguard' ? 'Check WireGuard' : next.action.label}
          </Button>
        </div>
      )}
    </div>
  );
}

const ROUTE_LABEL: Record<GatewayRoute, string> = {
  auto: 'RMS, then SMS',
  rms: 'RMS only',
  sms: 'SMS only',
};

function SendForm({
  router,
  action,
  onDone,
  onCancel,
}: {
  router: GatewayRouter;
  action: GatewayActionDef;
  onDone: (result: GatewaySendResult) => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const canSms = smsUsable(action, router);
  const routes: GatewayRoute[] = [
    ...(action.rms && canSms ? (['auto'] as const) : []),
    ...(action.rms ? (['rms'] as const) : []),
    ...(canSms ? (['sms'] as const) : []),
  ];
  const [route, setRoute] = useState<GatewayRoute>(routes[0] ?? 'rms');
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [fellThrough, setFellThrough] = useState<GatewaySendResult | null>(null);

  const usesSms = route !== 'rms' && action.sms !== null;
  const usesRms = route !== 'sms' && action.rms !== null;
  const wantsPassword = usesSms && Boolean(action.sms?.needsPassword);
  const passwordRequired = wantsPassword && route === 'sms';
  const passwordOk = password === '' ? !passwordRequired : /^[\x21-\x7e]{1,64}$/.test(password);
  const reasonRequired = action.effect === 'write' || (wantsPassword && password !== '');
  const reasonOk = !reasonRequired || reason.trim().length >= 5;
  const write = action.effect === 'write';

  const send = useMutation({
    mutationFn: () =>
      api.sendGatewayCommand(router.id, {
        action: action.id,
        route,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
        ...(wantsPassword && password ? { password } : {}),
      }),
    onSuccess: async (result) => {
      setPassword('');
      await queryClient.invalidateQueries({ queryKey: ['gateways', 'history', router.id] });
      await queryClient.invalidateQueries({ queryKey: ['gateways', 'log'] });
      // RMS could not run it and SMS was not tried: offer SMS here rather than
      // making the operator start over.
      if (result.smsAvailable && canSms) {
        setFellThrough(result);
        setRoute('sms');
        return;
      }
      onDone(result);
    },
    onError: () => setPassword(''),
  });

  return (
    <form
      className={cn('space-y-2.5 rounded-md border px-3 py-3', write ? 'border-caution/40 bg-caution/5' : 'border-line bg-raise')}
      onSubmit={(e) => {
        e.preventDefault();
        if (passwordOk && reasonOk) send.mutate();
      }}
    >
      <p className="text-sm">{action.description}</p>
      {action.id === 'wan-disable' && (
        <p className="text-xs text-caution">
          The site loses its wired internet until the WAN is enabled again, and every MB it uses meanwhile is billed
          cellular data. Remember to enable it once the line is fixed.
        </p>
      )}
      {fellThrough && (
        <div className="rounded-md border border-caution/40 bg-panel px-2.5 py-2 text-xs">
          {fellThrough.notes.map((n) => (
            <p key={n}>{n}</p>
          ))}
          <p className="mt-0.5 font-medium">Send it by SMS instead?</p>
        </div>
      )}

      {routes.length > 1 && !fellThrough && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-muted-foreground">Send via</span>
          <Segmented
            value={route}
            onChange={setRoute}
            options={routes.map((r) => ({ value: r, label: ROUTE_LABEL[r] }))}
            ariaLabel="Route"
          />
          {router.link === 'silent' && route !== 'sms' && (
            <span className="text-caution">RMS has not heard from this gateway lately — expect the SMS fallback.</span>
          )}
        </div>
      )}

      <div className="space-y-1 text-xs text-muted-foreground">
        {usesRms && action.rms && (
          <details>
            <summary className="cursor-pointer">
              {route === 'auto' ? 'First, over RMS' : 'Over RMS'}: a fixed shell command
            </summary>
            <code className="mt-1 block rounded bg-panel px-1.5 py-1 font-mono text-[11px] break-all text-foreground">
              {action.rms.preview}
            </code>
          </details>
        )}
        {usesSms && action.sms && (
          <div>
            {route === 'auto' ? 'If RMS cannot run it, texts' : 'Texts'} SIM {router.sim?.name ?? ''}:{' '}
            <code className="rounded bg-panel px-1 py-0.5 font-mono text-[11px] text-foreground">
              {action.sms.preview.replace('<serial>', router.serial ?? '<serial>')}
            </code>
          </div>
        )}
      </div>

      {wantsPassword && (
        <label className="block text-xs">
          <span className="text-muted-foreground">
            Gateway admin password{route === 'auto' ? ' — only used if RMS cannot run it' : ''}
          </span>
          <Input
            type="password"
            autoComplete="off"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1"
            aria-invalid={password !== '' && !passwordOk}
          />
          <span className="mt-0.5 block text-muted-foreground">
            {route === 'auto' && 'Leave empty to try RMS only. '}
            Sent inside the SMS only. Never stored, logged, or shown in the history.
            {password !== '' && !passwordOk && <span className="text-destructive"> One word, no spaces.</span>}
          </span>
        </label>
      )}
      <label className="block text-xs">
        <span className="text-muted-foreground">Reason{reasonRequired ? '' : ' (optional)'}</span>
        <Input
          value={reason}
          maxLength={500}
          onChange={(e) => setReason(e.target.value)}
          placeholder={write ? 'e.g. Customer reports no internet; wired link up but not passing traffic' : ''}
          className="mt-1"
        />
      </label>
      {send.error && <p className="text-xs text-destructive">{(send.error as Error).message}</p>}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={fellThrough ? () => onDone(fellThrough) : onCancel}>
          {fellThrough ? 'Not now' : 'Cancel'}
        </Button>
        <Button
          type="submit"
          size="sm"
          variant={write ? 'destructive' : 'default'}
          disabled={send.isPending || !passwordOk || !reasonOk || (passwordRequired && password === '')}
        >
          {send.isPending ? 'Sending…' : fellThrough ? `${action.label} by SMS` : action.label}
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">Each attempt is recorded in the audit log and this gateway's history before it is sent.</p>
    </form>
  );
}

function WireguardDashboardLink({ href }: { href: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
      title="Grafana watches every gateway's WireGuard tunnel; opens in a new tab"
    >
      WireGuard dashboard <ExternalLink className="h-3 w-3" />
    </a>
  );
}

function NotX5Badge() {
  return (
    <span title="Serves something other than X5, so it has no X5 server behind it — and none is expected.">
      <Badge variant="outline" className="ml-2 align-middle text-[10px]">
        Not X5
      </Badge>
    </span>
  );
}

function LinkBadge({ link }: { link: GatewayLink }) {
  const variant = link === 'mobile' ? 'destructive' : link === 'silent' ? 'warning' : link === 'wired' ? 'secondary' : 'outline';
  return (
    <Badge variant={variant} className="whitespace-nowrap">
      {LINK_LABEL[link]}
    </Badge>
  );
}

function Segmented<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
  ariaLabel: string;
}) {
  return (
    <div role="group" aria-label={ariaLabel} className="inline-flex rounded-md border border-line bg-panel p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'rounded px-2.5 py-1 text-xs font-medium transition-colors',
            value === o.value ? 'bg-accent-soft text-accent' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Tile({
  label,
  value,
  hint,
  tone,
  info,
}: {
  label: string;
  value: string;
  hint?: ReactNode;
  tone?: 'warn' | 'caution';
  info?: ReactNode;
}) {
  return (
    <Card className="px-4 py-3">
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        {label}
        {info && <InfoTip label={`About ${label}`}>{info}</InfoTip>}
      </div>
      <div
        className={cn(
          'text-2xl font-bold tabular-nums',
          tone === 'warn' && value !== '0' && 'text-warn',
          tone === 'caution' && value !== '0' && 'text-caution',
        )}
      >
        {value}
      </div>
      {hint && <div className="truncate text-xs text-muted-foreground">{hint}</div>}
    </Card>
  );
}

function EmptyRow({ cols, text }: { cols: number; text: string }) {
  return (
    <tr>
      <td colSpan={cols} className="px-4 py-6 text-center text-muted-foreground">
        {text}
      </td>
    </tr>
  );
}
